import React, { useState, useMemo } from 'react';
import { 
  List, 
  Box, 
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel 
} from '@mui/material';
import VenueListItem from './VenueListItem';
import { ReminderDialog } from './ReminderDialog';
import { toast } from 'react-toastify';

const VenueList = ({ venues, loading, location, selectedItem, onItemClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [openReminderDialog, setOpenReminderDialog] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);

  const handleReminderClick = (venue, event) => {
    event.stopPropagation(); // Prevent triggering the row click
    setSelectedVenue(venue);
    setOpenReminderDialog(true);
  };

  // Filter and sort venues
  const filteredAndSortedVenues = useMemo(() => {
    return venues
      .filter(venue => 
        venue.venue_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return a.venue_name.localeCompare(b.venue_name);
          case 'contract':
            if (!a.contract_end) return 1;
            if (!b.contract_end) return -1;
            return parseInt(a.contract_end) - parseInt(b.contract_end);
          case 'distance':
            if (!location) return 0;
            return calculateDistance(
              location.lat, 
              location.lng, 
              parseFloat(a.latitude), 
              parseFloat(a.longitude)
            ) - calculateDistance(
              location.lat, 
              location.lng, 
              parseFloat(b.latitude), 
              parseFloat(b.longitude)
            );
          default:
            return 0;
        }
      });
  }, [venues, searchTerm, sortBy, location]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box p={2} display="flex" gap={2}>
        <TextField
          fullWidth
          label="Search venues"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortBy}
            label="Sort by"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="contract">Contract Expiry</MenuItem>
            <MenuItem value="distance">Distance</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <List>
        {filteredAndSortedVenues.map((venue) => (
          <VenueListItem
            key={venue.venue_id}
            venue={venue}
            location={location}
            selected={selectedItem?.venue_id === venue.venue_id}
            onClick={() => onItemClick(venue)}
            onReminderClick={(e) => handleReminderClick(venue, e)}
          />
        ))}
      </List>

      <ReminderDialog
        open={openReminderDialog}
        onClose={() => setOpenReminderDialog(false)}
        businessName={selectedVenue?.venue_name || ''}
        venueId={selectedVenue?.venue_id}
        type="venue"
      />
    </Box>
  );
};

// Helper function to calculate distance between two points
const calculateDistance = (location, venue) => {
  if (!location?.lat || !location?.lng || !venue?.lat || !venue?.lng) return 0;
  
  const R = 6371; // Earth's radius in km
  const dLat = (venue.lat - location.lat) * Math.PI / 180;
  const dLon = (venue.lng - location.lng) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(location.lat * Math.PI / 180) * Math.cos(venue.lat * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

export default VenueList;
